import styled from '@emotion/styled';
import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { toastNotificationsService } from 'src/config/rootService';
import { BrandWrapper } from 'src/modules/onboarding/components/BrandWrapper';
import { Button } from 'src/shared/components/Button';
import { Http } from 'src/shared/services/Http';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

const Wrapper = styled.div({
    marginTop: 30,
    padding: 20,
    [mediaQueries.sm]: {
        top: '8%',
    },
});

const HeadingText = styled.h2(
    {
        color: color.pickledBluewood,
        marginBottom: 5,
    },
    font.black(font.size.largeExtended),
);

const DescriptionText = styled.p(
    {
        color: color.steelGray,
        marginBottom: 20,
    },
    font.medium(font.size.medium),
);

const ReasonList = styled.div({
    width: 500,
    marginBottom: 30,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
});

const Reason = styled.p({
    marginBottom: 20,
});

const Radio = styled.input({
    height: 16,
    width: 16,
    marginRight: 10,
    borderRadius: '100%',
    border: `1px solid ${color.steelGray}`,
});

const ReasonLabel = styled.label(
    {
        color: color.pickledBluewood,
    },
    font.medium(font.size.medium),
);

const unsubscribeReasons = [
    {
        translatedValue: 'user:email_unsubscribe.step_2.reason.not_interested',
        value: 'I no longer want to receive these emails',
    },
    {
        translatedValue: 'user:email_unsubscribe.step_2.reason.not_sign_up',
        value: 'I never signed up for this mailing list',
    },
    {
        translatedValue: 'user:email_unsubscribe.step_2.reason.too_frequent',
        value: 'These emails are too frequent',
    },
    {
        translatedValue: 'user:email_unsubscribe.step_2.reason.not_relevant',
        value: 'These emails are not relevant to me',
    },
];

interface MatchParams {
    email: string;
}

const EmailUnsubscribePage: React.FC<RouteComponentProps<MatchParams>> = ({ match }) => {
    const { t } = useTranslation();
    const { email } = match.params;
    const http = new Http();

    const [reason, setReason] = useState();
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(0);

    const toggleReason = newReason => {
        if (reason === newReason) {
            setReason(undefined);
        } else {
            setReason(newReason);
        }
    };

    const handleSubmit = () => {
        setLoading(true);

        const postData = {
            email,
        };

        if (reason) {
            postData['reason'] = reason;
        }

        http.POST_UNAUTHENTICATED('emails/unsubscribe_reason', postData)
            .then(() => {
                setStep(step + 1);
                setLoading(false);
            })
            .catch(() =>
                toastNotificationsService.renderErrorToast(
                    'Could not unsubscribe. Please email help@joinmodernhealth.com',
                ),
            );
    };

    return (
        <BrandWrapper>
            {step === 0 ? (
                <Wrapper>
                    <HeadingText>{t('user:email_unsubscribe.step_1.title')}</HeadingText>
                    <DescriptionText>
                        {t('user:email_unsubscribe.step_1.description')}
                    </DescriptionText>

                    <Button onClick={handleSubmit} type="accept">
                        {t('user:email_unsubscribe.step_1.unsubscribe')}
                    </Button>
                </Wrapper>
            ) : step === 1 ? (
                <Wrapper>
                    <HeadingText>{t('user:email_unsubscribe.step_2.title')}</HeadingText>
                    <DescriptionText>
                        {t('user:email_unsubscribe.step_2.description')}
                    </DescriptionText>
                    <ReasonList>
                        {unsubscribeReasons.map(({ value, translatedValue }) => (
                            <Reason key={value}>
                                <ReasonLabel>
                                    <Radio
                                        onChange={() => toggleReason(value)}
                                        type="radio"
                                        name="reason"
                                        value={value}
                                        checked={value === reason}
                                    />
                                    {t(translatedValue)}
                                </ReasonLabel>
                            </Reason>
                        ))}
                    </ReasonList>
                    <Button onClick={handleSubmit} loading={loading} type="accept">
                        {t('common.submit')}
                    </Button>
                </Wrapper>
            ) : (
                <Wrapper>
                    <HeadingText>{t('user:email_unsubscribe.feedback_thanks')}</HeadingText>
                </Wrapper>
            )}
        </BrandWrapper>
    );
};

export default EmailUnsubscribePage;
